import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { Button, Form, InputGroup, Badge, Spinner } from 'react-bootstrap';
import { toast } from 'react-toastify';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useAuth } from '../../context/AuthContext';
import { sendCopilotChat, executeCopilotAction, getCopilotContextPrompts } from '../../services/copilotService';

const MarkdownRenderer = ({ content }) => {
    if (!content) return null;
    return (
        <div className="copilot-markdown-content text-start">
            <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                components={{
                    p: ({ children }) => <p className="mb-2 last-mb-0 text-body">{children}</p>,
                    ul: ({ children }) => <ul className="ps-3 mb-2 text-body">{children}</ul>,
                    ol: ({ children }) => <ol className="ps-3 mb-2 text-body">{children}</ol>,
                    li: ({ children }) => <li className="mb-1 text-body">{children}</li>,
                    strong: ({ children }) => <strong className="fw-semibold text-body-emphasis">{children}</strong>,
                    code: ({ inline, children, ...props }) => (
                        <code className="bg-body-secondary text-primary px-1.5 py-0.5 rounded small" {...props}>
                            {children}
                        </code>
                    ),
                    pre: ({ children }) => (
                        <pre className="bg-body-secondary p-2 rounded small overflow-x-auto my-2 border">
                            {children}
                        </pre>
                    ),
                    table: ({ children }) => (
                        <div className="table-responsive my-2 rounded border">
                            <table className="table table-sm table-bordered mb-0 align-middle small">
                                {children}
                            </table>
                        </div>
                    ),
                    thead: ({ children }) => <thead className="bg-body-tertiary fw-semibold">{children}</thead>,
                    th: ({ children }) => <th className="p-1.5 bg-body-tertiary">{children}</th>,
                    td: ({ children }) => <td className="p-1.5">{children}</td>,
                    blockquote: ({ children }) => (
                        <blockquote className="border-start border-3 border-primary ps-2 text-secondary fst-italic my-2">
                            {children}
                        </blockquote>
                    ),
                    h1: ({ children }) => <h5 className="fw-bold mt-2 mb-2 text-primary">{children}</h5>,
                    h2: ({ children }) => <h6 className="fw-bold mt-2 mb-2 text-primary">{children}</h6>,
                    h3: ({ children }) => <h6 className="fw-bold mt-2 mb-1 text-primary-emphasis">{children}</h6>,
                    h4: ({ children }) => <h6 className="fw-semibold mt-1 mb-1">{children}</h6>,
                }}
            >
                {content}
            </ReactMarkdown>
        </div>
    );
};

const CopilotChat = () => {
    const { user } = useAuth();
    const location = useLocation();

    const [messages, setMessages] = useState([]);
    const [inputValue, setInputValue] = useState('');
    const [loading, setLoading] = useState(false);
    const [actionExecuting, setActionExecuting] = useState(false);
    const [contextInfo, setContextInfo] = useState({ type: 'general', title: 'หน้าจอทั่วไป' });
    const [prompts, setPrompts] = useState([]);
    const [copiedIdx, setCopiedIdx] = useState(null);
    const [viewMode, setViewMode] = useState('chat'); // 'chat' | 'guide'

    const chatEndRef = useRef(null);
    const textareaRef = useRef(null);

    // ตรวจจับ Context จาก URL
    useEffect(() => {
        const path = location.pathname;
        let detected = { type: 'general', title: 'หน้าจอทั่วไป' };

        if (path.includes('/customers/')) {
            const parts = path.split('/customers/');
            detected = { type: 'customer', id: parts[1], title: 'ข้อมูลลูกค้า' };
        } else if (path.includes('/opportunities/')) {
            const parts = path.split('/opportunities/');
            detected = { type: 'opportunity', id: parts[1], title: 'โอกาสการขาย / ดีล' };
        } else if (path.includes('/tickets/')) {
            const parts = path.split('/tickets/');
            detected = { type: 'ticket', id: parts[1], title: 'ใบงานซัพพอร์ต (Ticket)' };
        } else if (path.includes('/tasks')) {
            detected = { type: 'tasks', title: 'รายการงาน (Tasks)' };
        }

        setContextInfo(detected);

        // ดึง Dynamic Prompts ตาม Context
        getCopilotContextPrompts(detected.type, detected.id).then((res) => {
            if (Array.isArray(res) && res.length > 0) {
                setPrompts(res);
            }
        });
    }, [location.pathname]);

    const scrollToBottom = () => {
        chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    useEffect(() => {
        scrollToBottom();
    }, [messages, loading]);

    // Handle Send
    const handleSend = async (textToSend) => {
        const text = (typeof textToSend === 'string' ? textToSend : inputValue).trim();
        if (!text || loading) return;

        const userMsg = { role: 'user', content: text };
        const newHistory = [...messages, userMsg];

        setMessages(newHistory);
        setInputValue('');
        setLoading(true);

        try {
            const response = await sendCopilotChat(text, newHistory.slice(-8), contextInfo);

            const assistantMsg = {
                role: 'assistant',
                content: response.answer,
                action_proposal: response.action_proposal,
                suggestions: response.suggestions || []
            };

            setMessages((prev) => [...prev, assistantMsg]);
            if (response.suggestions && response.suggestions.length > 0) {
                setPrompts(response.suggestions);
            }
        } catch (error) {
            toast.error('ไม่สามารถติดต่อ Copilot ได้ในขณะนี้', { toastId: 'copilot-chat-err' });
            setMessages((prev) => [
                ...prev,
                {
                    role: 'assistant',
                    content: 'ขออภัยค่ะ เกิดข้อผิดพลาดในการประมวลผล กรุณาลองใหม่อีกครั้ง'
                }
            ]);
        } finally {
            setLoading(false);
        }
    };

    // Execute Action Proposal
    const handleExecuteAction = async (proposal, msgIndex) => {
        if (!proposal || actionExecuting) return;
        setActionExecuting(true);

        try {
            const res = await executeCopilotAction(proposal.action_type, proposal.payload);
            toast.success(res.message || 'ดำเนินการสำเร็จแล้วค่ะ', { toastId: 'copilot-action-success' });

            // แจ้งเตือนหน้าจอหลักให้รีเฟรชข้อมูลอัตโนมัติ (Real-time View Sync)
            window.dispatchEvent(new CustomEvent('copilot:action-executed', {
                detail: { action_type: proposal.action_type, payload: proposal.payload, result: res }
            }));

            // อัปเดตสถานะของข้อความนั้นว่า action สำเร็จแล้ว
            setMessages((prev) => {
                const updated = [...prev];
                if (updated[msgIndex]) {
                    updated[msgIndex] = {
                        ...updated[msgIndex],
                        action_completed: true,
                        action_result_message: res.message
                    };
                }
                return updated;
            });
        } catch (error) {
            toast.error('เกิดข้อผิดพลาดในการดำเนินการ', { toastId: 'copilot-action-fail' });
        } finally {
            setActionExecuting(false);
        }
    };

    // Copy Message
    const handleCopy = (text, idx) => {
        navigator.clipboard.writeText(text);
        setCopiedIdx(idx);
        setTimeout(() => setCopiedIdx(null), 2000);
    };

    // Auto-growing Textarea & Enter to Send
    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSend();
        }
    };

    return (
        <div className="d-flex flex-column h-100 bg-body text-start">
            {/* Context Badge Header */}
            <div className="px-3 py-2 border-bottom bg-body-tertiary d-flex align-items-center justify-content-between flex-shrink-0">
                {viewMode === 'guide' ? (
                    <div className="d-flex align-items-center gap-2">
                        <Button
                            variant="link"
                            size="sm"
                            className="text-body p-0 text-decoration-none"
                            onClick={() => setViewMode('chat')}
                            title="กลับไปหน้าแชท"
                        >
                            <i className="bi bi-arrow-left fs-6"></i>
                        </Button>
                        <span className="fw-semibold small text-body">วิธีสั่งงาน Copilot</span>
                    </div>
                ) : (
                    <div className="d-flex align-items-center gap-2 text-secondary small">
                        <span className="fw-medium text-body-secondary">
                            <i className="bi bi-chat-dots me-1 text-primary"></i>ห้องสนทนา
                        </span>
                    </div>
                )}

                <div className="d-flex align-items-center gap-2">
                    {viewMode === 'guide' ? (
                        <Button
                            variant="link"
                            size="sm"
                            className="text-primary small text-decoration-none p-0 fw-medium d-flex align-items-center gap-1"
                            onClick={() => setViewMode('chat')}
                        >
                            <span>กลับไปแชท</span>
                            <i className="bi bi-chevron-right small"></i>
                        </Button>
                    ) : (
                        <>
                            <Button
                                variant="link"
                                size="sm"
                                className="btn-action-ghost bg-body text-primary fw-medium rounded-pill px-3 py-1 border text-decoration-none d-flex align-items-center gap-1"
                                style={{ fontSize: '0.75rem' }}
                                title="ดูวิธีสั่งงาน"
                                onClick={() => setViewMode('guide')}
                            >
                                <i className="bi bi-question-circle"></i>
                                <span>วิธีใช้งาน</span>
                            </Button>
                            {messages.length > 0 && (
                                <Button
                                    variant="link"
                                    size="sm"
                                    className="text-secondary p-0 text-decoration-none ms-1"
                                    title="ล้างการสนทนา"
                                    onClick={() => setMessages([])}
                                >
                                    <i className="bi bi-trash3"></i>
                                </Button>
                            )}
                        </>
                    )}
                </div>
            </div>

            {viewMode === 'guide' ? (
                /* Sleek In-Panel Guide View */
                <div className="flex-grow-1 overflow-auto p-3 bg-body d-flex flex-column gap-3">
                    <p className="text-secondary small mb-0 lh-base">
                        พิมพ์สั่งงานด้วยภาษาพูดทั่วไป Copilot จะสรุปข้อมูลเป็นการ์ดให้ตรวจสอบก่อนบันทึกจริงเสมอ
                    </p>

                    {/* Section 1: Deals */}
                    <div className="p-3 rounded-3 border bg-body-tertiary">
                        <div className="d-flex align-items-center gap-2 mb-2">
                            <div className="d-flex align-items-center justify-content-center rounded-2 bg-primary-subtle text-primary flex-shrink-0" style={{ width: 28, height: 28 }}>
                                <i className="bi bi-briefcase"></i>
                            </div>
                            <span className="fw-semibold text-body small">จัดการดีล (Opportunities)</span>
                        </div>
                        <div className="d-flex flex-column gap-2">
                            <div className="bg-body border rounded-2 p-2">
                                <div className="fw-medium text-body small mb-1">ย้ายขั้นตอนดีล</div>
                                <div className="text-secondary small">
                                    &ldquo;ย้ายดีล KIT-POS ไปเจรจาต่อรอง&rdquo;
                                </div>
                            </div>
                            <div className="bg-body border rounded-2 p-2">
                                <div className="fw-medium text-body small mb-1">เปิดดีลใหม่พร้อมมูลค่าและวันปิด</div>
                                <div className="text-secondary small">
                                    &ldquo;สร้างดีลขายใหม่ ระบบบัญชี โตโยต้า 2 ล้าน ปิดสิ้นเดือนหน้า&rdquo;
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Section 2: Customers */}
                    <div className="p-3 rounded-3 border bg-body-tertiary">
                        <div className="d-flex align-items-center gap-2 mb-2">
                            <div className="d-flex align-items-center justify-content-center rounded-2 bg-info-subtle text-info-emphasis flex-shrink-0" style={{ width: 28, height: 28 }}>
                                <i className="bi bi-people"></i>
                            </div>
                            <span className="fw-semibold text-body small">ข้อมูลลูกค้า 360° (Customers)</span>
                        </div>
                        <div className="d-flex flex-column gap-2">
                            <div className="bg-body border rounded-2 p-2">
                                <div className="fw-medium text-body small mb-1">ค้นหาและดูข้อมูล 360°</div>
                                <div className="text-secondary small">
                                    &ldquo;ดูข้อมูลทั่วไปของพาราวินเซอร์&rdquo;
                                </div>
                            </div>
                            <div className="bg-body border rounded-2 p-2">
                                <div className="fw-medium text-body small mb-1">เพิ่มลูกค้าใหม่</div>
                                <div className="text-secondary small">
                                    &ldquo;สร้างลูกค้าใหม่ บริษัท สยามเทค คุณมานะ 089-xxx&rdquo;
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Section 3: Tasks */}
                    <div className="p-3 rounded-3 border bg-body-tertiary">
                        <div className="d-flex align-items-center gap-2 mb-2">
                            <div className="d-flex align-items-center justify-content-center rounded-2 bg-success-subtle text-success flex-shrink-0" style={{ width: 28, height: 28 }}>
                                <i className="bi bi-check2-square"></i>
                            </div>
                            <span className="fw-semibold text-body small">สั่งงานและเตือนความจำ (Tasks)</span>
                        </div>
                        <div className="d-flex flex-column gap-2">
                            <div className="bg-body border rounded-2 p-2">
                                <div className="fw-medium text-body small mb-1">สร้างงานเตือนความจำ</div>
                                <div className="text-secondary small">
                                    &ldquo;เตือนให้โทรหาลูกค้าพรุ่งนี้ 10 โมง&rdquo;
                                </div>
                            </div>
                            <div className="bg-body border rounded-2 p-2">
                                <div className="fw-medium text-body small mb-1">ตรวจสอบภาระงานทีม</div>
                                <div className="text-secondary small">
                                    &ldquo;สรุปภาระงานภาพรวมทีมวันนี้&rdquo;
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Section 4: Minimal Tips */}
                    <div className="p-3 rounded-3 border bg-body-tertiary">
                        <div className="d-flex align-items-center gap-2 mb-2">
                            <div className="d-flex align-items-center justify-content-center rounded-2 bg-warning-subtle text-warning-emphasis flex-shrink-0" style={{ width: 28, height: 28 }}>
                                <i className="bi bi-lightbulb"></i>
                            </div>
                            <span className="fw-semibold text-body small">เทคนิคการสั่งงาน</span>
                        </div>
                        <div className="bg-body border rounded-2 p-2 small text-secondary d-flex flex-column gap-2">
                            <div className="d-flex align-items-start gap-2">
                                <i className="bi bi-check2 text-success mt-0.5 flex-shrink-0"></i>
                                <span><strong className="text-body">ระบุวันเวลาได้:</strong> เช่น &ldquo;พรุ่งนี้&rdquo;, &ldquo;สิ้นเดือนนี้&rdquo;, &ldquo;สิ้นเดือนหน้า&rdquo;</span>
                            </div>
                            <div className="d-flex align-items-start gap-2">
                                <i className="bi bi-check2 text-success mt-0.5 flex-shrink-0"></i>
                                <span><strong className="text-body">ระบุจำนวนเงินได้:</strong> เช่น &ldquo;2 ล้าน&rdquo;, &ldquo;5 แสน&rdquo;, &ldquo;50,000&rdquo;</span>
                            </div>
                            <div className="d-flex align-items-start gap-2">
                                <i className="bi bi-check2 text-success mt-0.5 flex-shrink-0"></i>
                                <span><strong className="text-body">สั่งด้วยภาษาพูด:</strong> พิมพ์สั่งงานได้ตามปกติ ระบบจะสรุปการ์ดยืนยันให้เสมอ</span>
                            </div>
                        </div>
                    </div>
                </div>
            ) : (
                <>
                    {/* Chat Thread */}
                    <div className="flex-grow-1 overflow-auto p-3 d-flex flex-column gap-3 copilot-thread">
                        {messages.length === 0 ? (
                            <div className="text-center my-auto px-1 py-2">
                                <div className="bg-primary bg-opacity-10 text-primary rounded-4 d-inline-flex p-3 mb-2">
                                    <i className="bi bi-stars fs-3"></i>
                                </div>
                                <h6 className="fw-bold mb-1">KITbot Copilot 2.0</h6>
                                <p className="text-secondary small mb-3">
                                    ผู้ช่วยสั่งงาน CRM อัจฉริยะ ตอบคำถาม ดึงข้อมูล และสร้างรายการงานได้ทันที
                                </p>

                                <div className="d-flex flex-column gap-2 text-start">
                                    <div className="d-flex align-items-center justify-content-between mb-1">
                                        <small className="text-secondary fw-semibold">
                                            <i className="bi bi-grid me-1"></i>คำสั่งที่แนะนำ:
                                        </small>
                                        <button
                                            type="button"
                                            className="btn btn-link p-0 text-primary small text-decoration-none"
                                            style={{ fontSize: '0.75rem' }}
                                            onClick={() => setViewMode('guide')}
                                        >
                                            <i className="bi bi-journal-text me-1"></i>คู่มือทั้งหมด
                                        </button>
                                    </div>
                                    {prompts.map((p, i) => (
                                        <button
                                            key={i}
                                            type="button"
                                            className="btn btn-sm copilot-prompt-btn text-start rounded-pill py-2 px-3 d-flex align-items-center justify-content-between hover-shadow-sm w-100"
                                            onClick={() => handleSend(p)}
                                        >
                                            <span>{p}</span>
                                            <i className="bi bi-arrow-right-short fs-6"></i>
                                        </button>
                                    ))}
                                </div>
                            </div>
                ) : (
                    messages.map((msg, idx) => (
                        <div key={idx} className={`d-flex ${msg.role === 'user' ? 'justify-content-end' : 'justify-content-start'}`}>
                            <div className={`p-3 shadow-sm ${msg.role === 'user' ? 'bg-primary text-white rounded-user' : 'copilot-assistant-bubble rounded-assistant border w-100'}`} style={{ maxWidth: msg.role === 'user' ? '85%' : '100%' }}>
                                {msg.role === 'user' ? (
                                    <div style={{ whiteSpace: 'pre-wrap' }}>{msg.content}</div>
                                ) : (
                                    <div>
                                        <MarkdownRenderer content={msg.content} />

                                        {/* Action Proposal Card */}
                                        {msg.action_proposal && (
                                            <div className="mt-3 p-3 rounded-3 border copilot-action-card shadow-xs text-start">
                                                {/* Card Header */}
                                                <div className="d-flex align-items-center justify-content-between pb-2 mb-2 border-bottom border-secondary border-opacity-10 gap-2">
                                                    <div className="d-flex align-items-center gap-2 text-truncate">
                                                        <i className={`bi ${
                                                            msg.action_proposal.action_type === 'create_task'
                                                                ? 'bi-check2-square text-primary'
                                                                : msg.action_proposal.action_type === 'change_opportunity_stage' || msg.action_proposal.action_type === 'create_opportunity'
                                                                    ? 'bi-kanban text-primary'
                                                                    : 'bi-building text-primary'
                                                        } fs-5 flex-shrink-0`}></i>
                                                        <span className="fw-semibold text-body text-truncate" style={{ fontSize: '0.92rem' }}>
                                                            {msg.action_proposal.title}
                                                        </span>
                                                    </div>
                                                    <span className={`badge ${
                                                        msg.action_completed
                                                            ? 'bg-success-subtle text-success border border-success-subtle'
                                                            : 'bg-warning-subtle text-warning-emphasis border border-warning-subtle'
                                                    } text-nowrap fw-medium px-2 py-1 flex-shrink-0`} style={{ fontSize: '0.72rem' }}>
                                                        {msg.action_completed ? 'บันทึกแล้ว' : 'รอยืนยัน'}
                                                    </span>
                                                </div>
                                                {/* Details List (Spacious & Clean, No Nested Box) */}
                                                <div className="d-flex flex-column gap-1 small">
                                                        {msg.action_proposal.action_type === 'create_task' && (
                                                            <div className="d-flex flex-column gap-2">
                                                                <div>
                                                                    <span className="text-secondary me-1">หัวข้องาน:</span>
                                                                    <span className="fw-semibold text-body">{msg.action_proposal.payload.title}</span>
                                                                </div>
                                                                {msg.action_proposal.payload.customer_name && (
                                                                    <div>
                                                                        <span className="text-secondary me-1">ลูกค้า:</span>
                                                                        <span className="text-primary fw-medium">
                                                                            <i className="bi bi-building me-1"></i>
                                                                            {msg.action_proposal.payload.customer_name}
                                                                        </span>
                                                                    </div>
                                                                )}
                                                                <div className="d-flex flex-wrap gap-x-3 gap-y-1">
                                                                    <div>
                                                                        <span className="text-secondary me-1">วันที่เริ่ม:</span>
                                                                        <span className="text-body fw-medium">{msg.action_proposal.payload.start_date || '-'}</span>
                                                                    </div>
                                                                    <div className="ms-md-2">
                                                                        <span className="text-secondary me-1">กำหนดส่ง:</span>
                                                                        <span className="text-body fw-medium">{msg.action_proposal.payload.due_date || '-'}</span>
                                                                    </div>
                                                                </div>
                                                                <div className="d-flex align-items-center gap-2">
                                                                    <span className="text-secondary">ความสำคัญ:</span>
                                                                    <span className="badge bg-secondary-subtle text-body border border-secondary-subtle">
                                                                        ระดับ {msg.action_proposal.payload.priority}
                                                                    </span>
                                                                </div>
                                                                {msg.action_proposal.payload.description && msg.action_proposal.payload.description !== msg.action_proposal.payload.title && (
                                                                    <div className="text-secondary small mt-1 pt-1.5 border-top border-secondary border-opacity-10">
                                                                        <span className="fw-medium text-body">รายละเอียด:</span> {msg.action_proposal.payload.description}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        )}
                                                        {msg.action_proposal.action_type === 'change_opportunity_stage' && (
                                                            <div className="d-flex flex-column gap-2">
                                                                <div>
                                                                    <span className="text-secondary me-1">ดีล:</span>
                                                                    <span className="fw-semibold text-body">
                                                                        {msg.action_proposal.payload.opportunity_name}
                                                                    </span>
                                                                    {msg.action_proposal.payload.opportunity_code && (
                                                                        <span className="badge bg-secondary-subtle text-secondary ms-1.5 border border-secondary-subtle">
                                                                            {msg.action_proposal.payload.opportunity_code}
                                                                        </span>
                                                                    )}
                                                                </div>
                                                                {msg.action_proposal.payload.customer_name && msg.action_proposal.payload.customer_name !== '-' && (
                                                                    <div>
                                                                        <span className="text-secondary me-1">ลูกค้า:</span>
                                                                        <span className="text-body fw-medium">{msg.action_proposal.payload.customer_name}</span>
                                                                    </div>
                                                                )}
                                                                {msg.action_proposal.payload.value > 0 && (
                                                                    <div>
                                                                        <span className="text-secondary me-1">มูลค่าดีล:</span>
                                                                        <span className="text-success fw-bold">฿{Number(msg.action_proposal.payload.value).toLocaleString()}</span>
                                                                    </div>
                                                                )}
                                                                <div className="mt-1 pt-1.5 border-top border-secondary border-opacity-10 d-flex align-items-center gap-2">
                                                                    <span className="text-secondary">ขั้นตอน:</span>
                                                                    <span className="text-body-secondary">{msg.action_proposal.payload.current_stage_name || '-'}</span>
                                                                    <i className="bi bi-arrow-right text-primary"></i>
                                                                    <span className="text-primary fw-bold">{msg.action_proposal.payload.target_stage_name}</span>
                                                                </div>
                                                            </div>
                                                        )}
                                                        {msg.action_proposal.action_type === 'create_opportunity' && (
                                                            <div className="d-flex flex-column gap-2">
                                                                <div>
                                                                    <span className="text-secondary me-1">ชื่อดีล:</span>
                                                                    <span className="fw-semibold text-body">
                                                                        {msg.action_proposal.payload.opportunity_name}
                                                                    </span>
                                                                </div>
                                                                {msg.action_proposal.payload.customer_name && (
                                                                    <div>
                                                                        <span className="text-secondary me-1">ลูกค้า:</span>
                                                                        <span className="text-primary fw-medium">
                                                                            <i className="bi bi-building me-1"></i>
                                                                            {msg.action_proposal.payload.customer_name}
                                                                        </span>
                                                                    </div>
                                                                )}
                                                                <div className="d-flex flex-wrap gap-x-3 gap-y-1">
                                                                    <div>
                                                                        <span className="text-secondary me-1">มูลค่าดีล:</span>
                                                                        <span className="text-success fw-bold">
                                                                            ฿{Number(msg.action_proposal.payload.value || 0).toLocaleString()}
                                                                        </span>
                                                                    </div>
                                                                    {msg.action_proposal.payload.close_date && (
                                                                        <div className="ms-md-2">
                                                                            <span className="text-secondary me-1">กำหนดปิดดีล:</span>
                                                                            <span className="text-body fw-medium">{msg.action_proposal.payload.close_date}</span>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                                <div className="d-flex align-items-center gap-2 mt-0.5">
                                                                    <span className="text-secondary">ขั้นตอนเริ่มต้น:</span>
                                                                    <span className="badge bg-primary-subtle text-primary border border-primary-subtle">
                                                                        {msg.action_proposal.payload.stage_name || 'New'}
                                                                    </span>
                                                                    <span className="ms-2 text-secondary">ความสำคัญ:</span>
                                                                    <span className="badge bg-secondary-subtle text-body border border-secondary-subtle">
                                                                        ระดับ {msg.action_proposal.payload.priority || 3}
                                                                    </span>
                                                                </div>
                                                            </div>
                                                        )}
                                                        {msg.action_proposal.action_type === 'create_customer' && (
                                                            <div className="d-flex flex-column gap-2">
                                                                <div>
                                                                    <span className="text-secondary me-1">บริษัท / ลูกค้า:</span>
                                                                    <span className="fw-semibold text-primary">
                                                                        <i className="bi bi-building me-1"></i>
                                                                        {msg.action_proposal.payload.company_name}
                                                                    </span>
                                                                </div>
                                                                {msg.action_proposal.payload.contact_person && (
                                                                    <div>
                                                                        <span className="text-secondary me-1">ผู้ติดต่อ:</span>
                                                                        <span className="text-body fw-medium">
                                                                            <i className="bi bi-person me-1"></i>
                                                                            {msg.action_proposal.payload.contact_person}
                                                                        </span>
                                                                    </div>
                                                                )}
                                                                <div className="d-flex flex-wrap gap-x-3 gap-y-1">
                                                                    {msg.action_proposal.payload.phone && (
                                                                        <div>
                                                                            <span className="text-secondary me-1">เบอร์โทร:</span>
                                                                            <span className="text-body fw-medium">{msg.action_proposal.payload.phone}</span>
                                                                        </div>
                                                                    )}
                                                                    {msg.action_proposal.payload.email && (
                                                                        <div className="ms-md-2">
                                                                            <span className="text-secondary me-1">อีเมล:</span>
                                                                            <span className="text-body fw-medium">{msg.action_proposal.payload.email}</span>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                                {msg.action_proposal.payload.address && (
                                                                    <div className="text-secondary small">
                                                                        <span className="fw-medium text-body">ที่อยู่:</span> {msg.action_proposal.payload.address}
                                                                    </div>
                                                                )}
                                                                <div className="d-flex align-items-center gap-2 mt-0.5">
                                                                    <span className="text-secondary">สถานะเริ่มต้น:</span>
                                                                    <span className="badge bg-success-subtle text-success border border-success-subtle">
                                                                        {msg.action_proposal.payload.status || 'Lead'}
                                                                    </span>
                                                                </div>
                                                            </div>
                                                        )}
                                                </div>

                                                {/* Action Button or Completed Status */}
                                                {msg.action_completed ? (
                                                    <div className="mt-2.5 pt-2 border-top border-secondary border-opacity-10 d-flex align-items-center gap-2 text-success small fw-medium">
                                                        <i className="bi bi-check-circle-fill fs-6 flex-shrink-0"></i>
                                                        <span>{msg.action_result_message || 'ดำเนินการบันทึกเรียบร้อยแล้วค่ะ'}</span>
                                                    </div>
                                                ) : (
                                                    <div className="mt-3 pt-1">
                                                        <Button
                                                            variant="primary"
                                                            size="sm"
                                                            className="w-100 rounded-pill py-2 fw-medium d-flex align-items-center justify-content-center gap-2 shadow-sm"
                                                            disabled={actionExecuting}
                                                            onClick={() => handleExecuteAction(msg.action_proposal, idx)}
                                                        >
                                                            {actionExecuting ? (
                                                                 <>
                                                                    <Spinner animation="border" size="sm" />
                                                                    <span>กำลังบันทึกข้อมูล...</span>
                                                                </>
                                                            ) : (
                                                                <>
                                                                    <i className="bi bi-check2 fs-6"></i>
                                                                    <span>ยืนยันบันทึกข้อมูล</span>
                                                                </>
                                                            )}
                                                        </Button>
                                                    </div>
                                                )}
                                            </div>
                                        )}

                                        {/* Suggestion Chips */}
                                        {msg.suggestions && msg.suggestions.length > 0 && !msg.action_proposal && (
                                            <div className="mt-2 pt-2 border-top border-secondary border-opacity-10">
                                                <div className="small text-secondary fw-medium mb-1.5 d-flex align-items-center gap-1" style={{ fontSize: '0.8rem' }}>
                                                    <i className="bi bi-hand-index-thumb text-primary"></i> ตัวเลือกที่แนะนำ (คลิกเพื่อดำเนินการ):
                                                </div>
                                                <div className="d-flex flex-wrap gap-2">
                                                    {msg.suggestions.map((sug, sIdx) => (
                                                        <button
                                                            key={sIdx}
                                                            type="button"
                                                            className="btn btn-sm copilot-prompt-btn rounded-pill py-1 px-2.5 d-inline-flex align-items-center gap-1 hover-shadow-sm text-start"
                                                            style={{ fontSize: '0.8rem' }}
                                                            onClick={() => handleSend(sug)}
                                                            disabled={loading}
                                                        >
                                                            <span>{sug}</span>
                                                            <i className="bi bi-arrow-right-short"></i>
                                                        </button>
                                                    ))}
                                                </div>
                                            </div>
                                        )}

                                        {/* Footer copy button */}
                                        <div className="d-flex justify-content-end mt-2 pt-1 border-top border-secondary border-opacity-10">
                                            <button
                                                type="button"
                                                className="btn btn-sm btn-link p-0 text-secondary text-decoration-none opacity-75"
                                                style={{ fontSize: '0.75rem' }}
                                                onClick={() => handleCopy(msg.content, idx)}
                                                title="คัดลอกข้อความ"
                                            >
                                                <i className={`bi ${copiedIdx === idx ? 'bi-check2 text-success' : 'bi-clipboard'} me-1`}></i>
                                                {copiedIdx === idx ? 'คัดลอกแล้ว' : 'คัดลอก'}
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    ))
                )}

                {loading && (
                    <div className="d-flex justify-content-start">
                        <div className="copilot-assistant-bubble p-3 rounded-assistant border shadow-sm d-flex gap-1 align-items-center">
                            <span className="dot-typing"></span>
                            <span className="dot-typing" style={{ animationDelay: '0.2s' }}></span>
                            <span className="dot-typing" style={{ animationDelay: '0.4s' }}></span>
                        </div>
                    </div>
                )}
                <div ref={chatEndRef} />
            </div>

            {/* Input Bar */}
            <div className="p-3 border-top bg-body flex-shrink-0">
                <Form onSubmit={(e) => { e.preventDefault(); handleSend(); }}>
                    <InputGroup className="shadow-sm rounded-4 overflow-hidden border bg-body-tertiary">
                        <Form.Control
                            as="textarea"
                            rows={1}
                            ref={textareaRef}
                            placeholder="พิมพ์คำถาม หรือสั่งงาน CRM (Enter เพื่อส่ง)..."
                            className="border-0 px-3 py-2 shadow-none bg-transparent text-body resize-none"
                            style={{ minHeight: '40px', maxHeight: '100px' }}
                            value={inputValue}
                            onChange={(e) => setInputValue(e.target.value)}
                            onKeyDown={handleKeyDown}
                            disabled={loading}
                        />
                        <Button
                            variant="primary"
                            type="submit"
                            className="px-3 border-0 d-flex align-items-center"
                            disabled={!inputValue.trim() || loading}
                        >
                            <i className="bi bi-send-fill"></i>
                        </Button>
                    </InputGroup>
                </Form>
            </div>
        </>
    )}

            <style>{`
                .copilot-prompt-btn {
                    background-color: var(--bs-body-bg) !important;
                    border: 1px solid var(--bs-border-color) !important;
                    color: var(--bs-body-color) !important;
                    transition: all 0.15s ease-in-out;
                }

                .copilot-prompt-btn:hover,
                .copilot-prompt-btn:focus {
                    background-color: var(--bs-tertiary-bg) !important;
                    border-color: var(--bs-primary) !important;
                    color: var(--bs-primary) !important;
                }

                .copilot-prompt-btn:hover i {
                    color: var(--bs-primary) !important;
                    transform: translateX(2px);
                }

                .copilot-prompt-btn i {
                    transition: transform 0.15s ease-in-out, color 0.15s ease-in-out;
                }

                .copilot-assistant-bubble {
                    background-color: var(--bs-tertiary-bg);
                    color: var(--bs-body-color);
                    border-color: var(--bs-border-color) !important;
                }

                .copilot-markdown-content {
                    color: var(--bs-body-color);
                }

                .copilot-markdown-content li,
                .copilot-markdown-content p,
                .copilot-markdown-content span {
                    color: inherit;
                }

                .rounded-user {
                    border-radius: 18px 18px 0 18px;
                }

                .rounded-assistant {
                    border-radius: 18px 18px 18px 0;
                }

                .copilot-markdown-content p:last-child {
                    margin-bottom: 0;
                }

                .copilot-thread::-webkit-scrollbar {
                    width: 5px;
                }
                .copilot-thread::-webkit-scrollbar-thumb {
                    background: var(--bs-border-color);
                    border-radius: 10px;
                }

                .dot-typing {
                    width: 8px;
                    height: 8px;
                    border-radius: 50%;
                    background-color: var(--bs-primary);
                    animation: dotPulse 1.5s infinite ease-in-out;
                }

                @keyframes dotPulse {
                    0%, 100% { transform: scale(1); opacity: 0.3; }
                    50% { transform: scale(1.3); opacity: 1; }
                }

                .copilot-action-card {
                    background-color: var(--bs-body-bg) !important;
                    border-color: var(--bs-border-color) !important;
                }

                .copilot-action-details {
                    background-color: var(--bs-tertiary-bg) !important;
                    border-color: var(--bs-border-color) !important;
                    color: var(--bs-body-color) !important;
                }

                .resize-none {
                    resize: none;
                }
            `}</style>
        </div>
    );
};

export default CopilotChat;
